<script setup lang="ts">
import { Input } from '~/components/ui/input'
import { Button } from '~/components/ui/button'
import { useAiChat, buildSegments } from '~/composables/useAiChat'
import type { ChatMessage, AiChatResultKey, IAiChatComparisonItem } from '~/composables/useAiChat'
import {
  EntityAiChatAbout,
  EntityAiChatBank,
  EntityAiChatCredit,
  EntityAiChatDeposit,
  EntityAiChatDepositCalculation,
  EntityAiChatCreditCalculation,
  EntityAiChatCard,
  EntityAiChatExchangeRate,
  EntityAiChatExchangeRateProvider,
  EntityAiChatInsuranceProduct
} from '~/components/entity/ai-chat'
import { AirunLogo } from '~/components/ui/airun'

// Result keys with their own template branch instead of a per-item card:
// `comparison` (per-item dispatch by item_type + an action button) and
// `deposit_calculations` / `credit_calculations` (one table built from the whole group).
type StandaloneKey = 'comparison' | 'deposit_calculations' | 'credit_calculations'

// Card component per result group, rendered once per item.
const RESULT_CARDS: Record<Exclude<AiChatResultKey, StandaloneKey>, Component> = {
  organizations: EntityAiChatBank,
  credits: EntityAiChatCredit,
  deposits: EntityAiChatDeposit,
  cards: EntityAiChatCard,
  exchange_rates: EntityAiChatExchangeRate,
  exchange_rate_providers: EntityAiChatExchangeRateProvider,
  insurance_products: EntityAiChatInsuranceProduct,
  about: EntityAiChatAbout
}

// Comparison items reuse the regular product cards, dispatched by item_type.
const comparisonComponent = (item: IAiChatComparisonItem): Component =>
  RESULT_CARDS[item.item_type]

const { messages, isLoading, sendMessage } = useAiChat()
const { t } = useI18n()
const router = useRouter()
const { getPathWithLocale } = useLocalizedPath()
const compareStore = useCompareStore()

const openComparison = (items: readonly IAiChatComparisonItem[]) => {
  if (!items.length) return
  const type = items[0].item_type
  compareStore.setIds(type, items.map((i) => i.id))
  isOpen.value = false
  router.push(getPathWithLocale(`/compare/${type}`))
}

const isOpen = ref(false)
const inputText = ref('')
const messagesContainerRef = ref<HTMLElement | null>(null)
const inputRef = ref<{ inputRef: HTMLInputElement } | null>(null)
const popupRef = ref<HTMLElement | null>(null)
const bubbleRef = ref<HTMLElement | null>(null)

const GREETING = computed<ChatMessage>(() => ({
  role: 'assistant',
  text: t('widget_ai_chat.greeting')
}))

const allMessages = computed(() => {
  const msgs = messages.value.length === 0 ? [GREETING.value] : [...messages.value]
  return msgs.reverse().map((msg) => ({
    role: msg.role,
    text: msg.text,
    segments: msg.role === 'assistant' ? buildSegments(msg.text, msg.results) : []
  }))
})

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainerRef.value) messagesContainerRef.value.scrollTop = 0
  })
}

watch(() => messages.value.length, () => {
  if (isOpen.value) scrollToBottom()
})

watch(isOpen, (open) => {
  if (open) {
    scrollToBottom()
    nextTick(() => inputRef.value?.inputRef?.focus())
  }
})

const submit = async () => {
  const text = inputText.value
  if (!text.trim() || isLoading.value) return
  inputText.value = ''
  await sendMessage(text)
}

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    submit()
  }
}

const onEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') isOpen.value = false
}

const onClickOutside = (e: MouseEvent) => {
  if (!isOpen.value) return
  const target = e.target as Node
  if (!popupRef.value?.contains(target) && !bubbleRef.value?.contains(target)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('keydown', onEscape)
  document.addEventListener('mousedown', onClickOutside)
})
onUnmounted(() => {
  document.removeEventListener('keydown', onEscape)
  document.removeEventListener('mousedown', onClickOutside)
})
</script>

<template>
  <!-- Bubble -->
  <button
    ref="bubbleRef"
    class="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-transform hover:scale-105 hover:bg-blue-700"
    :aria-label="t('widget_ai_chat.open')"
    @click="isOpen = !isOpen"
  >
    <UiIcon v-if="!isOpen" name="message-square" class="h-6 w-6" />
    <UiIcon v-else name="x" class="h-6 w-6" />
  </button>

  <!-- Popup -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-4 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-4 opacity-0"
    >
      <div v-if="isOpen" ref="popupRef" class="AiChatPopup">
        <!-- Header -->
        <div class="flex items-center justify-between bg-blue-600 px-4 py-3">
          <div class="flex items-center gap-2">
            <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-white p-1">
              <AirunLogo class="h-6 w-6" />
            </span>
            <span class="font-semibold text-white">{{ t('widget_ai_chat.title') }}</span>
          </div>
          <button
            class="text-blue-200 transition-colors hover:text-white"
            :aria-label="t('widget_ai_chat.close')"
            @click="isOpen = false"
          >
            <UiIcon name="x" class="h-5 w-5" />
          </button>
        </div>

        <!-- Messages -->
        <div
          ref="messagesContainerRef"
          class="flex flex-1 flex-col-reverse gap-3 overflow-y-auto p-4"
          aria-live="polite"
          :aria-label="t('widget_ai_chat.messages_label')"
        >
          <!-- Loading indicator -->
          <div
            v-if="isLoading"
            class="flex max-w-[85%] gap-1 rounded-2xl rounded-tl-sm bg-gray-100 px-3 py-3"
          >
            <span class="h-2 w-2 animate-bounce rounded-full bg-gray-400" style="animation-delay: 0ms" />
            <span class="h-2 w-2 animate-bounce rounded-full bg-gray-400" style="animation-delay: 150ms" />
            <span class="h-2 w-2 animate-bounce rounded-full bg-gray-400" style="animation-delay: 300ms" />
          </div>

          <div
            v-for="(msg, idx) in allMessages"
            :key="idx"
            :class="['max-w-[85%]', msg.role === 'user' ? 'ml-auto' : '']"
          >
            <!-- User message -->
            <div
              v-if="msg.role === 'user'"
              class="rounded-2xl rounded-tr-sm bg-blue-600 px-3 py-2 text-sm text-white"
            >
              {{ msg.text }}
            </div>

            <!-- Assistant message: text + result groups in placeholder order -->
            <template v-else>
              <template v-for="(segment, i) in msg.segments" :key="i">
                <div
                  v-if="segment.kind === 'text'"
                  :class="[
                    'rounded-2xl rounded-tl-sm bg-gray-100 px-3 py-2 text-sm text-gray-800',
                    i > 0 ? 'mt-2' : ''
                  ]"
                >
                  <span v-hydrated-html="segment.html" />
                </div>

                <template v-else-if="segment.key === 'comparison'">
                  <div class="mt-2 flex flex-col gap-2">
                    <component
                      :is="comparisonComponent(item)"
                      v-for="item in segment.items"
                      :key="item.id"
                      :item="item"
                      @navigate="isOpen = false"
                    />
                  </div>
                  <Button
                    variant="outline-blue"
                    size="sm"
                    class="mt-2 w-full"
                    @click="openComparison(segment.items)"
                  >
                    {{ t('widget_ai_chat.compare_details') }}
                  </Button>
                </template>

                <EntityAiChatDepositCalculation
                  v-else-if="segment.key === 'deposit_calculations'"
                  :items="segment.items"
                />

                <EntityAiChatCreditCalculation
                  v-else-if="segment.key === 'credit_calculations'"
                  :items="segment.items"
                />

                <div v-else class="mt-2 flex flex-col gap-2">
                  <component
                    :is="RESULT_CARDS[segment.key]"
                    v-for="item in segment.items"
                    :key="item.url"
                    :item="item"
                    @navigate="isOpen = false"
                  />
                </div>
              </template>
            </template>
          </div>
        </div>

        <!-- Input -->
        <div class="flex items-center gap-2 border-t border-gray-100 p-3">
          <Input
            ref="inputRef"
            v-model="inputText"
            :placeholder="t('widget_ai_chat.placeholder')"
            :disabled="isLoading"
            :maxlength="2000"
            class="flex-1 text-sm"
            @keydown="onKeydown"
          />
          <Button
            size="sm"
            :disabled="isLoading || !inputText.trim()"
            class="h-9 w-9 shrink-0"
            @click="submit"
          >
            <UiIcon name="send" class="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
